/**
 * CustomerAccountPanel.tsx
 *
 * CRM 客户账号管理面板（Issue #96）
 *
 * 设计原则：
 * - 只使用 @jinzhuan/api-client（通过 useCrm hooks）
 * - 禁止手写 fetch/axios
 * - mutation 后使用后端返回值更新本地状态
 * - Dialog 使用 key={id} 强制重挂载
 * - 禁止展示 tenantId/orgId/passwordHash
 * - phone 只展示后端返回的 masked phone
 * - 权限门控：read/create/update/delete 分别控制可见性与操作
 * - 409/403/404 明确提示
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCustomerAccountByCustomerId,
  useCustomerAccountCreate,
  useCustomerAccountChangeStatus,
  useCustomerAccountSoftDelete,
  CUSTOMER_ACCOUNT_STATUS_LABELS,
  type CustomerAccountDto,
  type CustomerAccountStatus,
} from '@/hooks/useCrm';

// ─── Error message helpers ────────────────────────────────────────────────────

function formatApiError(status: number | undefined, message: string): string {
  if (status === 409) return '冲突（409）：账号已存在或手机号已被绑定，请确认后重试。';
  if (status === 403) return '权限不足（403）：您没有执行此操作的权限。';
  if (status === 404) return '未找到（404）：客户账号不存在或已被删除。';
  return message;
}

// ─── Create Account Dialog ────────────────────────────────────────────────────

interface CreateAccountDialogProps {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (account: CustomerAccountDto) => void;
}

function CreateAccountDialog({ customerId, open, onOpenChange, onCreated }: CreateAccountDialogProps) {
  const { loading, error, create } = useCustomerAccountCreate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await create({
      customerId,
      phone: phone.trim() || undefined,
      password: password.trim() || undefined,
    });
    if (result) {
      toast.success('客户账号已创建');
      onCreated(result);
      onOpenChange(false);
      setPhone('');
      setPassword('');
    }
    // error is shown inline via {error && ...} below
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-4 text-lg font-semibold">创建客户账号</Dialog.Title>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="account-phone">手机号（可选）</Label>
              <Input
                id="account-phone"
                data-testid="account-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="输入手机号"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="account-password">初始密码（可选）</Label>
              <Input
                id="account-password"
                data-testid="account-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="留空则仅支持微信登录"
              />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formatApiError(error.status, error.message)}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading} data-testid="account-create-submit">
                {loading ? '创建中…' : '创建账号'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Change Status Confirmation Dialog ───────────────────────────────────────

interface ChangeStatusDialogProps {
  account: CustomerAccountDto;
  targetStatus: CustomerAccountStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: (account: CustomerAccountDto) => void;
}

function ChangeStatusDialog({ account, targetStatus, open, onOpenChange, onChanged }: ChangeStatusDialogProps) {
  const { loading, error, changeStatus } = useCustomerAccountChangeStatus();

  const handleConfirm = async () => {
    const result = await changeStatus(account.id, { status: targetStatus });
    if (result) {
      const label = CUSTOMER_ACCOUNT_STATUS_LABELS[targetStatus];
      toast.success(`账号状态已变更为：${label}`);
      onChanged(result);
      onOpenChange(false);
    }
    // error shown inline
  };

  const actionLabel = targetStatus === 'suspended' ? '暂停账号' : '恢复账号';
  const actionDesc =
    targetStatus === 'suspended'
      ? '暂停后客户将无法登录，确认继续？'
      : '恢复后客户可重新登录，确认继续？';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-3 text-lg font-semibold">{actionLabel}</Dialog.Title>
          <p className="mb-4 text-sm text-muted-foreground">{actionDesc}</p>
          {error && (
            <p className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {formatApiError(error.status, error.message)}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              variant={targetStatus === 'suspended' ? 'destructive' : 'default'}
              disabled={loading}
              onClick={() => void handleConfirm()}
              data-testid="account-status-confirm"
            >
              {loading ? '处理中…' : '确认'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Soft Delete Confirmation Dialog ─────────────────────────────────────────

interface SoftDeleteDialogProps {
  account: CustomerAccountDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: (account: CustomerAccountDto) => void;
}

function SoftDeleteDialog({ account, open, onOpenChange, onDeleted }: SoftDeleteDialogProps) {
  const { loading, error, softDelete } = useCustomerAccountSoftDelete();

  const handleConfirm = async () => {
    const result = await softDelete(account.id);
    if (result) {
      toast.success('客户账号已删除（软删除）');
      onDeleted(result);
      onOpenChange(false);
    }
    // error shown inline
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card p-6 shadow-lg">
          <Dialog.Title className="mb-3 text-lg font-semibold">删除客户账号</Dialog.Title>
          <p className="mb-4 text-sm text-muted-foreground">
            删除后客户将无法登录。此操作为软删除，可由管理员恢复。确认继续？
          </p>
          {error && (
            <p className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {formatApiError(error.status, error.message)}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              variant="destructive"
              disabled={loading}
              onClick={() => void handleConfirm()}
              data-testid="account-delete-confirm"
            >
              {loading ? '删除中…' : '确认删除'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Main CustomerAccountPanel ────────────────────────────────────────────────

interface CustomerAccountPanelProps {
  customerId: string;
}

/**
 * CustomerAccountPanel
 *
 * 嵌入客户详情页的账号管理面板。
 * 权限门控：
 *   - crm.customer_account:read  → 面板可见，账号信息可查
 *   - crm.customer_account:create → "创建账号"按钮可见
 *   - crm.customer_account:update → suspend/reactivate 按钮可见
 *   - crm.customer_account:delete → "删除账号"按钮可见
 *
 * State machine:
 *   notFound=true  → "尚未绑定"面板（含 Create 按钮）
 *   account!=null  → 账号信息面板（含 suspend/reactivate/delete 按钮）
 *   fetchError!=null && !account && !notFound → 错误面板（含重试按钮）
 *
 * NOTE: fetchError from useCustomerAccountByCustomerId may remain non-null
 * after a successful create (since the initial GET returned 404). We guard
 * against this by checking !account in the error render branch.
 */
export default function CustomerAccountPanel({ customerId }: CustomerAccountPanelProps) {
  const { permissions } = useAuth();

  // ── Permission flags ──────────────────────────────────────────────────────
  const canRead = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_ACCOUNT_READ);
  const canCreate = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_ACCOUNT_CREATE);
  const canUpdate = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_ACCOUNT_UPDATE);
  const canDelete = hasPermission(permissions, CRM_PERMISSIONS.CUSTOMER_ACCOUNT_DELETE);

  // ── Data state ────────────────────────────────────────────────────────────
  const { loading, error: fetchError, fetch: fetchAccount } = useCustomerAccountByCustomerId();
  const [account, setAccount] = useState<CustomerAccountDto | null>(null);
  const [notFound, setNotFound] = useState(false);

  // ── Dialog states ─────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadAccount = useCallback(async () => {
    if (!canRead) return;
    setNotFound(false);
    const { data, isNotFound } = await fetchAccount(customerId);
    if (data) {
      setAccount(data);
    } else if (isNotFound) {
      setNotFound(true);
      setAccount(null);
    }
    // Non-404 errors: fetchError state is set by the hook; we render the error panel.
  }, [canRead, customerId, fetchAccount]);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  // ── Render: no read permission ────────────────────────────────────────────
  if (!canRead) {
    return null;
  }

  // ── Render: loading (initial) ─────────────────────────────────────────────
  if (loading && !account && !notFound) {
    return (
      <Card className="p-4">
        <h3 className="font-medium">客户账号</h3>
        <p className="mt-2 text-sm text-muted-foreground">加载中…</p>
      </Card>
    );
  }

  // ── Render: account exists ────────────────────────────────────────────────
  // Check account first so that a stale fetchError (from the initial 404 GET
  // before the account was created) does not shadow the newly-created account.
  if (account) {
    const isDeleted = account.deletedAt !== null && account.deletedAt !== undefined;
    const isSuspended = account.status === 'suspended';

    return (
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">客户账号</h3>
          <div className="flex gap-2">
            {canUpdate && !isDeleted && isSuspended && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReactivateOpen(true)}
                data-testid="account-reactivate-btn"
              >
                恢复账号
              </Button>
            )}
            {canUpdate && !isDeleted && !isSuspended && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSuspendOpen(true)}
                data-testid="account-suspend-btn"
              >
                暂停账号
              </Button>
            )}
            {canDelete && !isDeleted && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                data-testid="account-delete-btn"
              >
                删除账号
              </Button>
            )}
          </div>
        </div>

        {/* Account info — never show tenantId/orgId/passwordHash */}
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">账号 ID</dt>
          <dd className="truncate font-mono text-xs">{account.id}</dd>

          <dt className="text-muted-foreground">手机号</dt>
          <dd>{account.phone ?? '—'}</dd>

          <dt className="text-muted-foreground">状态</dt>
          <dd>
            {isDeleted ? (
              <span className="rounded-full bg-destructive/20 px-2 py-0.5 text-xs text-destructive">
                已删除
              </span>
            ) : (
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  isSuspended
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                {CUSTOMER_ACCOUNT_STATUS_LABELS[account.status as CustomerAccountStatus]}
              </span>
            )}
          </dd>

          <dt className="text-muted-foreground">创建时间</dt>
          <dd>{new Date(account.createdAt).toLocaleString('zh-CN')}</dd>

          <dt className="text-muted-foreground">最后更新</dt>
          <dd>{new Date(account.updatedAt).toLocaleString('zh-CN')}</dd>

          {isDeleted && account.deletedAt && (
            <>
              <dt className="text-muted-foreground">删除时间</dt>
              <dd>{new Date(account.deletedAt).toLocaleString('zh-CN')}</dd>
            </>
          )}
        </dl>

        {/* Dialogs — keyed to account.id to force remount on account change */}
        {suspendOpen && (
          <ChangeStatusDialog
            key={`suspend-${account.id}`}
            account={account}
            targetStatus="suspended"
            open={suspendOpen}
            onOpenChange={setSuspendOpen}
            onChanged={(updated) => {
              setAccount(updated);
              setSuspendOpen(false);
            }}
          />
        )}

        {reactivateOpen && (
          <ChangeStatusDialog
            key={`reactivate-${account.id}`}
            account={account}
            targetStatus="active"
            open={reactivateOpen}
            onOpenChange={setReactivateOpen}
            onChanged={(updated) => {
              setAccount(updated);
              setReactivateOpen(false);
            }}
          />
        )}

        {deleteOpen && (
          <SoftDeleteDialog
            key={`delete-${account.id}`}
            account={account}
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            onDeleted={(updated) => {
              setAccount(updated);
              setDeleteOpen(false);
            }}
          />
        )}
      </Card>
    );
  }

  // ── Render: fetch error (non-404, no account loaded) ─────────────────────
  if (fetchError && !notFound) {
    return (
      <Card className="p-4">
        <h3 className="font-medium">客户账号</h3>
        <p className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {formatApiError(fetchError.status, fetchError.message)}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => void loadAccount()}>
          重试
        </Button>
      </Card>
    );
  }

  // ── Render: no account bound yet (notFound=true or initial state) ─────────
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">客户账号</h3>
        {canCreate && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCreateOpen(true)}
            data-testid="account-create-btn"
          >
            创建账号
          </Button>
        )}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">该客户尚未绑定登录账号。</p>

      {/* Create Dialog — keyed to customerId to force remount */}
      {createOpen && (
        <CreateAccountDialog
          key={`account-create-${customerId}`}
          customerId={customerId}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(newAccount) => {
            setAccount(newAccount);
            setNotFound(false);
            setCreateOpen(false);
          }}
        />
      )}
    </Card>
  );
}
